import type { NutritionQueueQuery } from "@nova-ai/shared"
import type { PimWorkflowListQuery, PimWorkflowDetailQuery } from "@nova-ai/shared"
import type { RunsFilter } from "./use-runs"
import type { ProductsFilter } from "./use-products"
import type { ProductVisualImagesFilter } from "./use-product-visual-images"
import type { ProductVisualWorklistFilter } from "./use-product-visual-worklist"

export const queryKeys = {
  nutritionQueue: (query: NutritionQueueQuery) => ["nutrition-queue", query] as const,
  nutritionQueueSelection: (
    id: number | undefined,
    store: string | undefined,
    sku: string | undefined,
    scope?: string | undefined
  ) => ["nutrition-queue-selection", id, store, sku, scope] as const,
  nutritionQueueDetail: (id: number | undefined, status?: "failed" | undefined) =>
    ["nutrition-queue-detail", id, status] as const,
  nutritionQueueAttempts: (id: number | undefined) => ["nutrition-queue-attempts", id] as const,
  nutritionRecovery: (id: string) => ["nutrition-recovery", id] as const,
  pimWorkflowHistory: (filter: PimWorkflowListQuery) => ["pim-workflow-history", filter] as const,
  pimWorkflowHistoryDetail: (receiptId: number | undefined, filter: PimWorkflowDetailQuery) =>
    ["pim-workflow-history-detail", receiptId, filter] as const,
  authUser: ["auth", "user"] as const,
  conversations: (userId: number) => ["conversations", userId] as const,
  conversation: (userId: number, id: string | null) => ["conversations", userId, id] as const,
  runs: (filter: RunsFilter) => ["runs", filter] as const,
  relistRunInbox: ["runs", "automatic-inbox"] as const,
  runDetail: (slug: string) => ["run", slug, "detail"] as const,
  runSubmission: (slug: string, offset = 0) => ["run", slug, "submission", offset] as const,
  runStatus: (reference: string) => ["run", reference, "status"] as const,
  runFacets: (slug: string) => ["run", slug, "facets"] as const,
  runSetAside: (slug: string) => ["run", slug, "set-aside"] as const,
  runPreviewRecovery: (slug: string) => ["run", slug, "preview-recovery"] as const,
  automaticValueRecovery: (marketplace: string, runId?: number) =>
    runId === undefined
      ? (["automatic-value-recovery", marketplace] as const)
      : (["automatic-value-recovery", marketplace, runId] as const),
  productValueRecoveryChanges: (
    marketplace: string,
    generationId: number | undefined,
    offset: number,
    fingerprint?: string
  ) => ["product-value-recovery-changes", marketplace, generationId, offset, fingerprint] as const,
  productValueRecoveryPlan: (marketplace: string, originatingRunId?: number) =>
    [
      "product-values",
      "recovery-plan",
      marketplace,
      ...(originatingRunId === undefined ? [] : [originatingRunId]),
    ] as const,
  runFeedPreview: (slug: string) => ["run", slug, "feed-preview"] as const,
  runSkuPreview: (slug: string, configSku: string) =>
    ["run", slug, "sku", configSku, "preview"] as const,
  productOverview: (configSku: string) => ["product-overview", configSku] as const,
  productCostIdentities: (sku: string) => ["product-cost-identities", sku] as const,
  productCostCoverage: ["product-cost-coverage"] as const,
  productCostHistory: (sku: string, store: string, from?: string, to?: string) =>
    ["product-cost-history", sku, store, from, to] as const,
  productValues: (configSku: string, marketplace: string | undefined) =>
    ["product-values", configSku, marketplace] as const,
  productConfigVisuals: (configSku: string, store: string) =>
    ["product-config-visuals", configSku, store] as const,
  productPhotos: (configSku: string, store: string) =>
    ["product-photos", configSku, store] as const,
  productVisualImages: (filter: ProductVisualImagesFilter) =>
    ["product-visual-images", filter] as const,
  productVisualWorklist: (filter: ProductVisualWorklistFilter) =>
    ["product-visual-worklist", filter] as const,
  galleryStudioPlanInputs: (configSku: string, store?: string) =>
    ["gallery-studio", configSku, "plan-inputs", store] as const,
  galleryStudioPlanInputsPrefix: (configSku: string) =>
    ["gallery-studio", configSku, "plan-inputs"] as const,
  galleryStudioBootstrapInputs: (configSku: string, store: string) =>
    ["gallery-studio", configSku, "bootstrap-inputs", store] as const,
  galleryStudioCandidates: (configSku: string, revisionId: string) =>
    ["gallery-studio", configSku, "revision", revisionId, "candidates"] as const,
  galleryStudioDocumentCandidates: (configSku: string, documentId: string) =>
    ["gallery-studio", configSku, "document", documentId, "candidates"] as const,
  galleryStudioDocumentCandidatesPrefix: (configSku: string) =>
    ["gallery-studio", configSku, "document"] as const,
  galleryDraftJob: (configSku: string, jobId: string) =>
    ["gallery-studio", configSku, "draft-job", jobId] as const,
  visualDirections: ["visual-directions"] as const,
  visualDirection: (directionId: string) => ["visual-directions", directionId] as const,
  visualDirectionProposals: (directionId: string) =>
    ["visual-directions", directionId, "proposals"] as const,
  visualDirectionFontCoverage: (assetId: string) =>
    ["visual-directions", "font", assetId, "glyph-coverage"] as const,
  productVisualDirection: (configSku: string) =>
    ["visual-directions", "product", configSku] as const,
  products: (filter: ProductsFilter) => ["products", filter] as const,
  users: ["users"] as const,
  scheduledJobs: ["scheduled-jobs"] as const,
  scheduledJobPreview: (id: string, cron: string, zone: string) =>
    ["schedule-preview", id, cron, zone] as const,
  scheduledJobRuns: (id: string) => ["scheduled-jobs", id, "runs"] as const,
  scheduledJobRevisions: (id: string) => ["scheduled-jobs", id, "revisions"] as const,
  searchMarketplaces: ["search-marketplaces"] as const,
  searchRankingOverview: (marketplace: string) => ["search-rankings", marketplace] as const,
  searchRankingKeyword: (marketplace: string, phrase: string, runId = "") =>
    ["search-rankings", marketplace, "keyword", phrase, runId] as const,
  searchRankingOpportunities: (marketplace: string, phrase: string, from: string, to: string) =>
    ["search-rankings", marketplace, "keyword", phrase, "opportunities", from, to] as const,
  searchProductRankings: (marketplace: string, sku: string) =>
    ["search-rankings", marketplace, "product", sku] as const,
  baselinkerRefresh: (marketplace: string) => ["baselinker-refresh", marketplace] as const,
  baselinkerRefreshJob: (id: string) => ["baselinker-refresh", "job", id] as const,
}
